import { useMemo, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import moment from 'moment'

import driverMapApi from 'apis/disApi/driverMapApi'
import { isEqual } from 'lodash'

const mostRecentDriveKey = {
	prefixKey: ['getMostRecentDrive'],
	drive: (
		tileKey,
		userId,
		fromDate,
		toDate,
		showHistory,
		useMostRecent,
		repeatReloadMapSeconds
	) => [
		...mostRecentDriveKey.prefixKey,
		tileKey,
		userId,
		fromDate,
		toDate,
		showHistory,
		useMostRecent,
		repeatReloadMapSeconds,
	],
}

export function useMostRecentDriveQuery({
	tileKey,
	driveId,
	useMostRecent,
	userId,
	fromDate,
	toDate,
	showHistory,
	repeatReloadMapSeconds,
}) {
	const environment = useSelector((state) => state.environment)
	const queryKey = useMemo(() => {
		if (driveId || !useMostRecent || !userId) return null
		return mostRecentDriveKey.drive(
			tileKey,
			userId,
			fromDate,
			toDate,
			showHistory,
			useMostRecent,
			repeatReloadMapSeconds
		)
	}, [
		tileKey,
		driveId,
		fromDate,
		toDate,
		showHistory,
		userId,
		useMostRecent,
		repeatReloadMapSeconds,
	])

	const [mostRecentDrive, setMostRecentDrive] = useState(null)
	const [mostRecentHeartBeat, setMostRecentHeartBeat] = useState(null)

	const { data, isFetching } = useQuery({
		queryKey: queryKey,
		queryFn: async () => {
			const mostRecentDriveResponse = await driverMapApi.getDrivesByUserId({
				user_id: userId,
				end_after: fromDate,
				start_before: toDate,
				isMostRecent: true,
				showHistory,
				userOffset: environment?.userCurrent?.time?.timezone?.offset || 0,
				token: environment?.apiToken,
			})

			const mostRecentHeartBeatResponse = await driverMapApi.getDriverHeartBeat({
				user_id: userId,
				after: fromDate,
				before: toDate,
				showHistory,
				isMostRecent: true,
				token: environment?.apiToken,
			})

			return {
				drive: mostRecentDriveResponse?.data?.data[0] || {},
				heartBeat: mostRecentHeartBeatResponse?.data?.[0] || {},
			}
		},
		refetchInterval: useMostRecent ? repeatReloadMapSeconds * 1000 : null, // Convert seconds to milliseconds
		enabled: !!queryKey && !!environment?.apiToken, // Only fetch if enabled
	})

	useEffect(() => {
		let newMostRecentDrive = null
		let newMostRecentHeartBeat = null
		if (useMostRecent) {
			if (isFetching) return

			const drive = data?.drive || null
			const heartBeat = data?.heartBeat || null

			if (!showHistory) {
				const mostRecentHeartBeatTimestamp = heartBeat?.timestamp || null
				const currentDriveTimestamp = drive?.end_date_timestamp || null

				let isBeforeMostRecentHeartBeat = false
				const mostRecentHeartBeatTimestampMoment = moment.utc(mostRecentHeartBeatTimestamp)
				const currentDriveTimestampMoment = moment.utc(currentDriveTimestamp)

				if (mostRecentHeartBeatTimestampMoment.isValid() && currentDriveTimestampMoment.isValid()) {
					isBeforeMostRecentHeartBeat = currentDriveTimestampMoment.isBefore(
						mostRecentHeartBeatTimestampMoment
					)
				}

				if (isBeforeMostRecentHeartBeat) {
					newMostRecentDrive = null
					newMostRecentHeartBeat = heartBeat
				} else {
					newMostRecentDrive = drive
					newMostRecentHeartBeat = null
				}
			} else {
				newMostRecentDrive = data?.drive || null
				newMostRecentHeartBeat = data?.heartBeat || null
			}
		}

		setMostRecentDrive((prev) =>
			!isEqual(newMostRecentDrive?.id, prev?.id) ? newMostRecentDrive : prev
		)
		setMostRecentHeartBeat((prev) =>
			!isEqual(newMostRecentHeartBeat, prev) ? newMostRecentHeartBeat : prev
		)
	}, [data?.drive, data?.heartBeat, isFetching, useMostRecent, showHistory])

	return {
		mostRecentDrive,
		mostRecentHeartBeat,
	}
}
